import { Scope, VariableEdit } from 'src/components/formComposed/WorkspaceVariablesForm/types';
import {
  VARIABLE_NAME_REGEX,
  VARIABLE_MIN_VALUE,
  VARIABLE_MIN_VALUE_ERROR,
} from 'src/components/formComposed/WorkspaceVariablesForm/util';
import {
  CsvImportGroups,
  CSVVarHeaders,
  CsvVariable,
  CsvWorkspaceGroups,
} from 'src/components/VariableCSVReader/types';
import { getStandardVariableNames } from 'src/components/VariableCSVReader/util';
import { isValidNumberValue } from 'src/utils/csv';
import * as Yup from 'yup';

export const filterCsvVariablesByGroups = (
  variables: CsvVariable[],
  groups: CsvImportGroups | undefined,
  workspaceGroups: CsvWorkspaceGroups | undefined,
): CsvVariable[] => {
  const excludedNames = new Set<string>();

  if (groups) {
    Object.values(groups).forEach((group) => {
      if (group.action === 'exclude') {
        group.variables.forEach((v: CsvVariable) => excludedNames.add(v.name));
      }
    });
  }

  if (workspaceGroups) {
    Object.values(workspaceGroups).forEach((group) => {
      if (group.action === 'exclude') {
        group.variables.forEach((v: CsvVariable) => excludedNames.add(v.name));
      }
    });
  }

  return variables.filter((v) => !excludedNames.has(v.name));
};

export const csvToVariableEdit = (
  csvVariables: CsvVariable[],
  scope: Scope,
  wVars: VariableEdit[],
  originalVariables: VariableEdit[],
): VariableEdit[] => {
  return csvVariables.map((csvVar) => {
    const workspaceVar = wVars?.find((wv) => wv.name === csvVar.name);
    const existsInWorkspaceVars = !!workspaceVar;
    const existingAssessmentVar = originalVariables.find((v) => v.name === csvVar.name);

    if (scope.type === 'assessment') {
      if (existingAssessmentVar) {
        return {
          ...existingAssessmentVar,
          value: csvVar.value ?? 0,
          currency: csvVar.currency,
          _isFromCSV: true,
        };
      } else if (existsInWorkspaceVars && workspaceVar) {
        return {
          ...workspaceVar,
          id: Date.now() + Math.random(),
          value: csvVar.value ?? 0,
          currency: csvVar.currency,
          workspace_variable: false,
          isGlobal: false,
          isInUse: [],
          _isNewOverwrite: true,
          _isFromCSV: true,
        };
      }
    }

    if (scope.type === 'workspace') {
      const existingVar = originalVariables.find((v) => v.name === csvVar.name);
      if (existingVar) {
        return {
          ...existingVar,
          value: csvVar.value ?? 0,
          currency: csvVar.currency,
          _isFromCSV: true,
        };
      }
    }

    return {
      id: Date.now() + Math.random(),
      name: csvVar.name,
      value: csvVar.value ?? 0,
      currency: csvVar.currency,
      workspace_variable: scope.type === 'workspace',
      isGlobal: false,
      isInUse: [],
      alias: null,
      _isNew: true,
      _isFromCSV: true,
    };
  });
};

const standardVariableNames = getStandardVariableNames();

export const csvVariableSchema = Yup.object({
  [CSVVarHeaders.Name]: Yup.string().test({
    name: 'name-validation',
    test: function (value) {
      if (!value || value.trim().length === 0) {
        return this.createError({ message: 'Name is required' });
      }
      if (!VARIABLE_NAME_REGEX.test(value)) {
        return this.createError({ message: 'Contains unsupported characters' });
      }
      return true;
    },
  }),
  [CSVVarHeaders.Value]: Yup.string().test({
    name: 'value-validation',
    test: function (value) {
      const name = this.parent[CSVVarHeaders.Name];
      const isStandardVariable = standardVariableNames.includes(name?.trim());

      if (isStandardVariable) {
        if (value === undefined || value === null || value === '') return true;
        if (!isValidNumberValue(value)) {
          return this.createError({ message: 'Value must be a valid number' });
        }
        const num = parseFloat(value);
        if (isNaN(num) || num === 0) return true;
        if (num < VARIABLE_MIN_VALUE) {
          return this.createError({ message: VARIABLE_MIN_VALUE_ERROR });
        }
        return true;
      }

      if (value === undefined || value === null || value === '') {
        return this.createError({ message: 'Value is required' });
      }
      if (!isValidNumberValue(value)) {
        return this.createError({ message: 'Value must be a valid number' });
      }
      const num = parseFloat(value);
      if (isNaN(num)) {
        return this.createError({ message: 'Value must be a valid number' });
      }
      if (num === 0) return true;
      if (num < VARIABLE_MIN_VALUE) {
        return this.createError({ message: VARIABLE_MIN_VALUE_ERROR });
      }
      return true;
    },
  }),
  [CSVVarHeaders['Currency (yes/no)']]: Yup.string().test({
    name: 'currency-validation',
    test: function (value) {
      const name = this.parent[CSVVarHeaders.Name];
      const isStandardVariable = standardVariableNames.includes(name?.trim());

      if (isStandardVariable) return true;

      if (!value) {
        return this.createError({ message: 'Currency field is required' });
      }
      if (value !== 'yes' && value !== 'no') {
        return this.createError({ message: 'Currency must be "yes" or "no"' });
      }
      return true;
    },
  }),
});
